import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import ViewerBanner from "@/components/ViewerBanner";
import UploadProgress from "@/components/UploadProgress";
import { useIsViewer } from "@/lib/permissions";
import { toast } from "sonner";
import { UploadSimple, Palette } from "@phosphor-icons/react";

const SWATCHES = ["#2563EB", "#0EA5E9", "#059669", "#D97706", "#DC2626", "#7C3AED", "#DB2777", "#0F172A"];

export default function CompanyProfile() {
  const viewer = useIsViewer();
  const [c, setC] = useState({});
  const [progress, setProgress] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/company/profile").then((r) => setC(r.data.company || {}));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put("/company/profile", c);
      await api.put("/company/branding", { hero_color: c.hero_color || null });
      toast.success("Saved");
    } catch { toast.error("Failed"); }
  };
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProgress(0);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/company/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress((evt.loaded / evt.total) * 100);
        },
      });
      setProgress(100);
      setC({ ...c, logo: r.data.path });
      toast.success("Logo updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed — please retry in a moment.");
    } finally {
      setTimeout(() => setProgress(null), 800);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const brand = c.hero_color || "#2563EB";
  const ro = !!viewer;
  return (
    <div className="max-w-3xl space-y-5">
      {viewer && <ViewerBanner />}
      <div><div className="label-caps">Business</div><h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Company profile</h1></div>

      <div className="card-tactile overflow-hidden">
        <div className="h-32 relative" style={{ background: `linear-gradient(135deg, ${brand}, ${brand}CC)` }}>
          <div className="absolute inset-0 flex items-end p-5">
            <div className="w-20 h-20 bg-white rounded-md border-4 border-white shadow-lg overflow-hidden">
              {c.logo ? <img src={fileUrl(c.logo)} alt="logo" className="w-full h-full object-contain" data-testid="brand-logo-img" /> : <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">no logo</div>}
            </div>
            <div className="ml-4 pb-2">
              <div className="font-display text-2xl font-extrabold text-white">{c.company_name || "Your company"}</div>
              <div className="text-white/80 text-sm">{c.industry}</div>
            </div>
          </div>
        </div>
        <div className="p-5 grid md:grid-cols-2 gap-4">
          {!ro && (
            <div className="md:col-span-2 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
                <Button data-testid="logo-upload-btn" variant="outline" onClick={() => fileRef.current?.click()}><UploadSimple size={16} className="mr-1.5"/> Upload logo</Button>
                <div className="flex items-center gap-2 flex-wrap">
                  <Palette size={16} className="text-slate-500"/>
                  {SWATCHES.map((s) => (
                    <button key={s} type="button" onClick={() => setC({ ...c, hero_color: s })} data-testid={`swatch-${s.replace('#','')}`}
                      className={`w-7 h-7 rounded-md border-2 ${c.hero_color === s ? "border-slate-900" : "border-white ring-1 ring-slate-200"}`} style={{ background: s }} />
                  ))}
                  <Input data-testid="brand-hex" value={c.hero_color || ""} onChange={(e)=>setC({...c, hero_color: e.target.value})} placeholder="#2563EB" className="h-9 w-28" />
                </div>
              </div>
              <UploadProgress percent={progress} label={progress === 100 ? "Upload complete" : "Uploading logo"} />
            </div>
          )}
          <div className="md:col-span-2"><Label className="label-caps">Company name</Label><Input readOnly={ro} data-testid="co-name" value={c.company_name || ""} onChange={(e)=>setC({...c, company_name: e.target.value})} className="h-11 mt-1.5" /></div>
          <div><Label className="label-caps">Industry</Label><Input readOnly={ro} data-testid="co-industry" value={c.industry || ""} onChange={(e)=>setC({...c, industry: e.target.value})} className="h-11 mt-1.5" /></div>
          <div><Label className="label-caps">Registration #</Label><Input readOnly={ro} data-testid="co-reg" value={c.registration_number || ""} onChange={(e)=>setC({...c, registration_number: e.target.value})} className="h-11 mt-1.5" /></div>
          <div className="md:col-span-2"><Label className="label-caps">Address</Label><Input readOnly={ro} data-testid="co-address" value={c.address || ""} onChange={(e)=>setC({...c, address: e.target.value})} className="h-11 mt-1.5" /></div>
          <div><Label className="label-caps">Postcode</Label><Input readOnly={ro} data-testid="co-postcode" value={c.postcode || ""} onChange={(e)=>setC({...c, postcode: e.target.value})} className="h-11 mt-1.5" /></div>
          <div><Label className="label-caps">Contact name</Label><Input readOnly={ro} data-testid="co-contact" value={c.contact_name || ""} onChange={(e)=>setC({...c, contact_name: e.target.value})} className="h-11 mt-1.5" /></div>
          <div className="md:col-span-2"><Label className="label-caps">Description</Label><Textarea readOnly={ro} data-testid="co-desc" rows={4} value={c.description || ""} onChange={(e)=>setC({...c, description: e.target.value})} className="mt-1.5" /></div>
        </div>
      </div>
      {!ro && <Button data-testid="co-save-btn" onClick={save} className="bg-blue-600 hover:bg-blue-700 rounded-md h-12 px-8">Save changes</Button>}
    </div>
  );
}
